import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api";
import { EggEventLog, EggEventLogsResponse, EggEventStatus } from "../../types";

const STATUS_CONFIG: Record<EggEventStatus, { label: string; color: "success" | "error" | "warning" | "default" }> = {
  dispatched: { label: "Inviato", color: "success" },
  error: { label: "Errore", color: "error" },
  duplicate: { label: "Duplicato", color: "warning" },
  no_rule: { label: "Nessuna regola", color: "default" },
};

const PAGE_LIMIT = 20;

type StatusFilter = EggEventStatus | "all";

interface Props {
  ruleId: string;
}

export default function EggRuleAuditLog({ ruleId }: Props) {
  const [logs, setLogs] = useState<EggEventLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedLog, setSelectedLog] = useState<EggEventLog | null>(null);

  const fetchLogs = useCallback(
    async (opts: { append?: boolean; cursor?: string | null } = {}) => {
      const { append = false, cursor: cursorParam = null } = opts;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = { limit: PAGE_LIMIT };
        if (statusFilter !== "all") params.status = statusFilter;
        if (cursorParam) params.cursor = cursorParam;

        const res = await apiClient.get(`/v1/egg/rules/${ruleId}/logs`, { params });
        const payload: EggEventLogsResponse | EggEventLog[] = res.data?.data ?? res.data;
        const newLogs = Array.isArray(payload) ? payload : payload.logs ?? [];
        const nextCursor = Array.isArray(payload) ? undefined : payload.next_cursor;

        setLogs((prev) => (append ? [...prev, ...newLogs] : newLogs));
        setCursor(nextCursor ?? null);
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento dei log");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruleId, statusFilter]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleStatusChange = (e: SelectChangeEvent<StatusFilter>) => {
    setStatusFilter(e.target.value as StatusFilter);
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleLoadMore = () => {
    if (cursor) fetchLogs({ append: true, cursor });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Attività recente
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="egg-log-status-filter">Status</InputLabel>
            <Select
              labelId="egg-log-status-filter"
              label="Status"
              value={statusFilter}
              onChange={handleStatusChange}
              disabled={loading || loadingMore}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="dispatched">Inviato</MenuItem>
              <MenuItem value="error">Errore</MenuItem>
              <MenuItem value="duplicate">Duplicato</MenuItem>
              <MenuItem value="no_rule">Nessuna regola</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Ricarica">
            <span>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={loading || loadingMore}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Nessun evento registrato per questa regola.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data/ora</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell width={120}>Status</TableCell>
                  <TableCell>Event ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => {
                  const status = STATUS_CONFIG[log.status];
                  return (
                    <React.Fragment key={log.id}>
                      <TableRow
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => setSelectedLog(log)}
                      >
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {new Date(log.created_at).toLocaleString("it-IT")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {log.action ?? "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status?.label ?? log.status}
                            size="small"
                            color={status?.color ?? "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                            {log.event_id ?? "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {log.status === "error" && log.error && (
                        <TableRow
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => setSelectedLog(log)}
                        >
                          <TableCell colSpan={4} sx={{ py: 0.5, pl: 4, bgcolor: "error.50" }}>
                            <Typography variant="caption" color="error.main" fontFamily="monospace">
                              {log.error}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {cursor && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                onClick={handleLoadMore}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
              >
                {loadingMore ? "Caricamento..." : "Carica altri"}
              </Button>
            </Box>
          )}
        </>
      )}

      <Drawer
        anchor="right"
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
      >
        {selectedLog && <LogDetail log={selectedLog} onClose={() => setSelectedLog(null)} />}
      </Drawer>
    </Box>
  );
}

function LogDetail({ log, onClose }: { log: EggEventLog; onClose: () => void }) {
  const status = STATUS_CONFIG[log.status];
  const navigate = useNavigate();
  // A chatbot_flow dispatch carries tenant_id + user_phone (see BE T-017), which
  // identify the conversation doc — enable a jump to the flow execution.
  const canOpenExecution =
    log.target_type === "chatbot_flow" && !!log.tenant_id && !!log.user_phone;
  const openExecution = () => {
    const convId = `${log.tenant_id}_${log.user_phone}`;
    const flowQ = log.flow_id ? `&flowId=${log.flow_id}` : "";
    navigate(
      `/chatbot/monitoring/conversations/${encodeURIComponent(convId)}?tenantId=${log.tenant_id}${flowQ}`
    );
  };
  return (
    <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={status?.label ?? log.status}
            size="small"
            color={status?.color ?? "default"}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {new Date(log.created_at).toLocaleString("it-IT")}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      <Section title="Evento">
        <Field label="Event ID" value={log.event_id} mono />
        <Field label="Action" value={log.action} mono />
        <Field label="Rule" value={log.rule_name ?? log.rule_id ?? undefined} mono />
        <Field label="Target type" value={log.target_type ?? undefined} mono />
        {log.company && <Field label="Company" value={log.company} mono />}
      </Section>

      {canOpenExecution && (
        <Section title="Esecuzione flow">
          <Field label="Flow" value={log.flow_id} mono />
          <Field label="Utente" value={log.user_phone} mono />
          <Button
            variant="outlined"
            size="small"
            startIcon={<TimelineIcon fontSize="small" />}
            onClick={openExecution}
            sx={{ alignSelf: "flex-start", mt: 0.5 }}
          >
            Vai all'esecuzione del flow
          </Button>
        </Section>
      )}

      {log.status === "error" && log.error && (
        <Section title="Errore">
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: "error.50",
              color: "error.main",
              fontSize: "0.75rem",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              border: "1px solid",
              borderColor: "error.light",
              borderRadius: 1,
            }}
          >
            {log.error}
          </Box>
        </Section>
      )}

      <Section title="Payload ricevuto">
        {log.raw_payload && Object.keys(log.raw_payload).length > 0 ? (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: "grey.100",
              fontSize: "0.75rem",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 320,
              overflow: "auto",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
            }}
          >
            {JSON.stringify(log.raw_payload, null, 2)}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nessun payload disponibile per questo evento.
          </Typography>
        )}
      </Section>

      {log.http_call && (
        <Section title="Chiamata verso il partner (outbound)">
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: "grey.100",
              fontSize: "0.75rem",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 480,
              overflow: "auto",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
            }}
          >
            {JSON.stringify(log.http_call, null, 2)}
          </Box>
        </Section>
      )}
    </Box>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {title}
      </Typography>
      <Stack spacing={0.75}>{children}</Stack>
    </Box>
  );
}

function Field({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontFamily={mono ? "monospace" : undefined}
        sx={{ wordBreak: "break-all" }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}
